import { InfoCircleOutlined } from '@ant-design/icons';
import { message, Space, Spin, Typography } from 'antd';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MentionUser } from '../../components/editor/RichTextEditor';
import { getUserComponent } from '../../helpers/user';
import type { KanbanTask } from '../../stores/KanbanTaskStores';
import { isVisibleOnBoard } from '../../stores/kanban/boardRules';
import { useStore } from '../../stores/root-store-context';
import { useCurrentProject } from '../Projects/ProjectContext';
import Board from './Board';
import BoardToolbar from './BoardToolbar';
import ExportTimeLogsModal, { canExportTimeLogs } from './ExportTimeLogsModal';
import './KanbanTasks.css';
import ListView from './ListView';
import MoveTasksModal from './MoveTasksModal';
import StatusModal from './StatusModal';
import TaskDetailsDrawer from './TaskDetails/TaskDetailsDrawer';
import { useBoardDnd } from './useBoardDnd';
import { applyBoardFilters, useBoardFilters } from './useBoardFilters';
import { useDeleteStatus } from './useDeleteStatus';
import { useQuickAdd } from './useQuickAdd';
import { useReadOnly } from './useReadOnly';
import { useTaskDrawer } from './useTaskDrawer';

const { Title, Text } = Typography;

export type KanbanBoardPageProps = {
  /** The public (shared-link) board. Falls back to what `ProjectContext` reports. */
  isAnonymous?: boolean;
};

/**
 * The Kanban board page — the route component of both the authenticated board and the public one.
 * It owns the project-level wiring (data loading, the task actions, the modals) and hands the
 * rendering to `BoardToolbar`, `Board`/`Column` and `ListView`, and the URL ↔ drawer link to
 * `useTaskDrawer`.
 */
const KanbanBoardPage: React.FC<KanbanBoardPageProps> = observer(({ isAnonymous: isAnonymousProp }) => {
  const { t } = useTranslation();
  const { applicationStore, employeesStore, kanbanTaskStore } = useStore();
  const { project, projectId, isAnonymous: projectIsAnonymous, isLoading: isProjectLoading } = useCurrentProject();
  const isAnonymous = isAnonymousProp ?? projectIsAnonymous;

  /** One store serves both boards (Task 9), so the board and the drawer show the same task. */
  const store = kanbanTaskStore;

  const { filters, setFilter, clearFilters, activeCount } = useBoardFilters();
  const [showDeletedTasks, setShowDeletedTasks] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const isReadOnly = useReadOnly(project, applicationStore.user, isAnonymous);

  useEffect(() => {
    // The user list is authenticated-only; the public board has no company session.
    if (isAnonymous) return;
    employeesStore.getUsers().catch((error) => console.error('Failed to load users', error));
  }, [employeesStore, isAnonymous]);

  useEffect(() => {
    // The public board's statuses and tasks are loaded by `AnonymousPage`, which uses that same
    // call as its token check (401 → /link-expired), so loading them again here would double it.
    if (!projectId || isAnonymous) return;
    store.getProjectStatuses(projectId).catch(console.error);
    store.getProjectTasks(projectId).catch(console.error);
  }, [projectId, isAnonymous, store]);

  useEffect(() => {
    if (!projectId || !showDeletedTasks) return;
    store.getDeletedProjectTasks(projectId).catch(console.error);
  }, [projectId, showDeletedTasks, store]);

  /**
   * `projectStatuses` is a mobx observable array that `createStatus` pushes into *in place*, so its
   * identity is not a safe memo key: a new column would neither invalidate the memo nor, once the
   * memo body stops running, keep this observer subscribed to the array at all. Reading the
   * contents on every render fixes both, and keys the memo on what actually changed.
   */
  const statusesKey = store.projectStatuses
    .map((status) => `${status.id}:${status.index}:${status.name}:${status.isFinalStatus}`)
    .join('|');

  const statuses = useMemo(
    () => [...store.projectStatuses].sort((a, b) => a.index - b.index),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusesKey],
  );

  /**
   * What the board and the list read. On the board, final-status tasks older than a week drop off —
   * the rule the retired `kanban/project` route applied on the server, now `boardRules` here.
   */
  const visibleTasks = useMemo(() => {
    if (showDeletedTasks) return store.deletedProjectTasks;
    // A deep link (or a delete in another tab) can leave a deleted task in the map.
    const deletedIds = new Set(store.deletedProjectTasks.map((task) => String(task.id)));
    const live = store.projectTasks.filter((task) => !deletedIds.has(String(task.id)));
    return filters.view === 'kanban' ? live.filter((task) => isVisibleOnBoard(task, statuses)) : live;
  }, [showDeletedTasks, store.deletedProjectTasks, store.projectTasks, statuses, filters.view]);

  const filteredTasks = useMemo(
    () =>
      applyBoardFilters(visibleTasks, filters).map((task) => ({
        ...task,
        assignee: employeesStore.users.find((user) => String(user.id) === String(task.assigneeId)),
      })),
    [visibleTasks, filters, employeesStore.users],
  );

  const tasksByStatus = useMemo(() => {
    const grouped: Record<string, KanbanTask[]> = {};
    filteredTasks.forEach((task) => {
      if (!grouped[task.statusId]) grouped[task.statusId] = [];
      grouped[task.statusId].push(task);
    });
    return grouped;
  }, [filteredTasks]);

  /** The drawer follows the URL — see `useTaskDrawer`; `taskUrl` is also what "copy link" copies. */
  const {
    selectedTaskId,
    taskUrl,
    openTask: openTaskDetails,
    close: closeTaskDetails,
  } = useTaskDrawer(
    store,
    isAnonymous,
    applicationStore.company?.Tenant,
    // The page that owns this board configures the api client in its own effect, which React
    // runs *after* this child's. Resolving a url before that lands on the wrong routes.
    !isProjectLoading,
  );

  const handleDeleteTask = useCallback(
    async (id: string): Promise<void> => {
      try {
        await store.deleteTask(id);
        message.success(t('TaskDeleted'));
        closeTaskDetails();
      } catch (error) {
        console.error(error);
        message.error(t('FailedToDeleteTask'));
      }
    },
    [store, t, closeTaskDetails],
  );

  const handleDeleteTaskPermanent = useCallback(
    async (id: string): Promise<void> => {
      try {
        await store.deleteTaskPermanent(id);
        message.success(t('TaskDeleted'));
        closeTaskDetails();
      } catch (error) {
        console.error(error);
        message.error(t('FailedToDeleteTask'));
      }
    },
    [store, t, closeTaskDetails],
  );

  const handleRestoreTask = useCallback(
    async (id: string): Promise<void> => {
      try {
        await store.restoreTask(id);
        message.success(t('TaskRestored'));
        setShowDeletedTasks(false);
        closeTaskDetails();
      } catch (error) {
        console.error(error);
        message.error(t('FailedToRestoreSelected'));
      }
    },
    [store, t, closeTaskDetails],
  );

  const handleCopyTaskLink = useCallback(
    (task: KanbanTask) => {
      const link = `${window.location.origin}${taskUrl(task.publicId || task.id)}`;
      navigator.clipboard
        .writeText(link)
        .then(() => message.success(t('TaskLinkCopied')))
        .catch(() => message.error(t('FailedToCopyLink')));
    },
    [taskUrl, t],
  );

  const handleCreateStatus = useCallback(
    async (name: string, isFinalStatus: boolean): Promise<void> => {
      if (!projectId) return;
      const result = await store.createStatus(name, projectId, isFinalStatus);
      if (result) {
        message.success(t('ColumnCreated'));
        setIsStatusModalOpen(false);
      }
    },
    [projectId, store, t],
  );

  /** The one `deleteStatus` call site. A non-empty column (409 `StatusNotEmpty`) opens `MoveTasksModal` instead of a toast. */
  const { requestDelete, moveRequest, confirmMove, cancelMove } = useDeleteStatus(store);

  const handleDeleteStatus = useCallback(
    (statusId: string): void => {
      void requestDelete(Number(statusId));
    },
    [requestDelete],
  );

  /** `@`-mention candidates for the description and comments; the public board has no user list. */
  const mentionUsers = useMemo<MentionUser[]>(
    () =>
      isAnonymous
        ? []
        : employeesStore.users.map((user) => ({
            id: String(user.id),
            label: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
          })),
    [isAnonymous, employeesStore.users],
  );

  const assignableUsers = useMemo(
    () =>
      isAnonymous
        ? []
        : employeesStore.users
            .slice()
            .sort((a, b) =>
              `${a.firstName} ${a.lastName}`
                .toLowerCase()
                .localeCompare(`${b.firstName} ${b.lastName}`.toLowerCase()),
            ),
    [isAnonymous, employeesStore.users],
  );

  const renderTaskUser = useCallback(
    (userId: string) =>
      getUserComponent(
        applicationStore.siteConfig?.baseCloudinary ?? '',
        employeesStore.users.find((user) => String(user.id) === String(userId)),
        applicationStore.company?.Tenant ?? 'anonymous',
        30,
      ),
    [applicationStore.siteConfig, applicationStore.company, employeesStore.users],
  );

  const dnd = useBoardDnd(store, statuses, isReadOnly);
  const quickAdd = useQuickAdd(
    store,
    projectId,
    applicationStore.user?.id ? String(applicationStore.user.id) : undefined,
  );

  return (
    <div className="kanban-tasks-container">
      <div className="kanban-content kanban-font-settings">
        <Spin spinning={store.isLoading}>
          <BoardToolbar
            filters={filters}
            setFilter={setFilter}
            clearFilters={clearFilters}
            activeCount={activeCount}
            users={isAnonymous ? [] : employeesStore.users}
            canExport={!isAnonymous && canExportTimeLogs(project, applicationStore.user)}
            onExport={() => setIsExportModalOpen(true)}
            t={t}
          />

          <div className="project-header kanban-headers">
            <Space size="large">
              <Title level={4} className="project-title">
                {project?.title || 'Project'}
              </Title>
            </Space>
          </div>

          {isReadOnly && (
            <div className="read-only-banner">
              <InfoCircleOutlined style={{ marginRight: 8 }} />
              <Text type="secondary">
                {t('ViewOnlyAccess')} - {t('YouHaveReadOnlyPermissions')}
              </Text>
            </div>
          )}

          {filters.view === 'kanban' ? (
            <Board
              store={store}
              statuses={statuses}
              tasksByStatus={tasksByStatus}
              isReadOnly={isReadOnly}
              isAnonymous={isAnonymous}
              dnd={dnd}
              quickAdd={quickAdd}
              onOpenTask={openTaskDetails}
              onAddStatus={() => setIsStatusModalOpen(true)}
              onDeleteStatus={handleDeleteStatus}
              t={t}
              assignableUsers={assignableUsers}
              baseCloudinary={applicationStore.siteConfig?.baseCloudinary ?? ''}
            />
          ) : (
            <ListView
              tasks={filteredTasks}
              statuses={statuses}
              users={employeesStore.users}
              onTaskClick={openTaskDetails}
              t={t}
              showDeletedTasks={showDeletedTasks}
              setShowDeletedTasks={setShowDeletedTasks}
              store={store}
              isReadOnly={isReadOnly}
              isAnonymous={isAnonymous}
              baseCloudinary={applicationStore.siteConfig?.baseCloudinary ?? ''}
            />
          )}

          <StatusModal
            open={isStatusModalOpen}
            onCancel={() => setIsStatusModalOpen(false)}
            onCreate={handleCreateStatus}
            t={t}
          />

          <MoveTasksModal
            open={moveRequest != null}
            statuses={store.projectStatuses}
            fromStatusId={moveRequest?.fromStatusId ?? 0}
            taskCount={moveRequest?.taskCount ?? 0}
            onConfirm={async (targetStatusId) => {
              await confirmMove(targetStatusId);
            }}
            onCancel={cancelMove}
          />

          <ExportTimeLogsModal
            open={isExportModalOpen}
            onCancel={() => setIsExportModalOpen(false)}
            exporting={store.isExportingTimeLogs}
            projectId={projectId ?? ''}
            userId={applicationStore.user?.id ? String(applicationStore.user.id) : ''}
            onExport={store.exportTimeLogsAndDownload}
            t={t}
          />

          <TaskDetailsDrawer
            taskId={selectedTaskId}
            open={!!selectedTaskId}
            onClose={closeTaskDetails}
            store={store}
            isAnonymous={isAnonymous}
            isReadOnly={isReadOnly}
            projectId={projectId ?? ''}
            mentionUsers={mentionUsers}
            assignableUsers={assignableUsers}
            baseCloudinary={applicationStore.siteConfig?.baseCloudinary ?? ''}
            currentUserId={applicationStore.user?.id ? String(applicationStore.user.id) : undefined}
            renderUser={renderTaskUser}
            onDelete={!isReadOnly ? handleDeleteTask : undefined}
            onRestore={showDeletedTasks ? handleRestoreTask : undefined}
            onDeletePermanent={showDeletedTasks ? handleDeleteTaskPermanent : undefined}
            onCopyLink={handleCopyTaskLink}
            projectName={project?.title}
          />
        </Spin>
      </div>
    </div>
  );
});

export default KanbanBoardPage;
